import {
  ADULT_AGE_YEARS,
  GUARDIAN_RELATION,
  ageInYears,
  familyLinkNamesGuardian,
  fatherInFullName,
  isInfant,
  type CountryProfile,
  type FacilityKind,
  type GuardianRelation,
  type NamePart,
  type RegisterPatientInput,
  type UpdatePatientInput,
} from '@his/shared';

export type RegistrationMode = 'quick' | 'full';

export const REGISTRATION_MODES: readonly RegistrationMode[] = ['quick', 'full'];

export type RegistrationSectionId = 'identity' | 'personal' | 'birth' | 'contact' | 'guardian' | 'emergency' | 'payer';

export interface RegistrationContext {
  profile: CountryProfile;
  facility: FacilityKind;
  keepsFullRecords: boolean;
  mode: RegistrationMode;
  needsBranch: boolean;
}

export interface PatientAgeFacts {
  age: number | null;
  isMinor: boolean;
  infant: boolean;
  showGuardianDetails: boolean;
}

export function ageFacts(values: Partial<Pick<RegisterPatientInput, 'dob' | 'approximateAgeYears' | 'guardianRelation'>>, now: Date = new Date()): PatientAgeFacts {
  const approximate = typeof values.approximateAgeYears === 'number' && !Number.isNaN(values.approximateAgeYears) ? values.approximateAgeYears : null;
  const dob = values.dob || null;
  const age = ageInYears(dob, approximate);
  const isMinor = age !== null && age < ADULT_AGE_YEARS;
  return {
    age,
    isMinor,
    infant: isInfant(dob, approximate, now),
    showGuardianDetails: isMinor || (values.guardianRelation ?? 'self') !== 'self',
  };
}

export type AgeUnit = 'days' | 'months' | 'years';

export function ageSummary(dob: string | null | undefined, approximateAgeYears: number | null | undefined, now: Date = new Date()): { unit: AgeUnit; count: number; estimated: boolean } | null {
  if (dob) {
    const born = Date.parse(`${dob}T00:00:00.000Z`);
    if (Number.isNaN(born) || born > now.getTime()) return null;
    const days = Math.floor((now.getTime() - born) / 86_400_000);
    if (days < 31) return { unit: 'days', count: days, estimated: false };
    const months = Math.floor(days / 30.4375);
    if (months < 24) return { unit: 'months', count: months, estimated: false };
    return { unit: 'years', count: ageInYears(dob, null) ?? Math.floor(days / 365.25), estimated: false };
  }
  if (typeof approximateAgeYears === 'number' && !Number.isNaN(approximateAgeYears)) return { unit: 'years', count: approximateAgeYears, estimated: true };
  return null;
}

export type BirthWeightBand = 'extremely_low' | 'very_low' | 'low' | 'normal' | 'high';

export function birthWeightBand(grams: number | null | undefined): BirthWeightBand | null {
  if (typeof grams !== 'number' || Number.isNaN(grams) || grams <= 0) return null;
  if (grams < 1000) return 'extremely_low';
  if (grams < 1500) return 'very_low';
  if (grams < 2500) return 'low';
  if (grams > 4000) return 'high';
  return 'normal';
}

export type GestationBand = 'preterm' | 'term' | 'post_term';

export function gestationBand(weeks: number | null | undefined): GestationBand | null {
  if (typeof weeks !== 'number' || Number.isNaN(weeks) || weeks <= 0) return null;
  if (weeks < 37) return 'preterm';
  if (weeks >= 42) return 'post_term';
  return 'term';
}

export function nameParts(profile: CountryProfile, mode: RegistrationMode, keepsFullRecords: boolean): NamePart[] {
  const parts: NamePart[] = profile.names.parts.includes('first') ? profile.names.parts : ['first', 'last'];
  return parts.filter((part) => {
    if (part !== 'father') return true;
    if (!keepsFullRecords) return false;
    return mode === 'full' || fatherInFullName(profile);
  });
}

function filled(value: unknown): boolean {
  if (typeof value === 'number') return !Number.isNaN(value);
  if (typeof value === 'string') return value.trim().length > 0;
  return value !== undefined && value !== null;
}

export function identityRequired(context: Pick<RegistrationContext, 'profile' | 'facility'>, facts: PatientAgeFacts): boolean {
  return context.profile.identity.capture[context.facility] === 'required' && !facts.isMinor;
}

export function identityShown(context: Pick<RegistrationContext, 'profile' | 'facility' | 'mode'>): boolean {
  const capture = context.profile.identity.capture[context.facility];
  if (capture === 'hidden') return false;
  return context.mode === 'full' || capture === 'required';
}

export function identityRecorded(values: Pick<RegisterPatientInput, 'cnic' | 'idType' | 'idMissingReason'>): boolean {
  if (values.idType === 'no_document') return Boolean(values.idMissingReason);
  return filled(values.cnic);
}

export function missingRequired(values: Partial<RegisterPatientInput>, context: RegistrationContext, facts: PatientAgeFacts): string[] {
  const missing: string[] = [];
  if (context.needsBranch && !filled(values.branchId)) missing.push('branchId');
  if (!filled(values.firstName)) missing.push('firstName');
  if (!values.sex) missing.push('sex');
  if (context.keepsFullRecords && !filled(values.dob) && !filled(values.approximateAgeYears)) missing.push('dob');
  if (identityShown(context) && values.idType === 'no_document' && !values.idMissingReason) missing.push('idMissingReason');
  if (identityShown(context) && identityRequired(context, facts) && values.idType !== 'no_document' && !filled(values.cnic)) missing.push('cnic');
  if (context.keepsFullRecords && guardianNeeded(values, facts)) {
    if (!filled(values.guardianName)) missing.push('guardianName');
    if (!filled(values.guardianPhone)) missing.push('guardianPhone');
  }
  return missing;
}

export function guardianNeeded(values: Partial<RegisterPatientInput>, facts: PatientAgeFacts): boolean {
  if (!facts.showGuardianDetails) return false;
  if (facts.isMinor && familyLinkNamesGuardian(values.family) && !filled(values.guardianName)) return false;
  return true;
}

export interface SectionProgress {
  id: RegistrationSectionId;
  done: boolean;
}

export function sectionProgress(values: Partial<RegisterPatientInput>, context: RegistrationContext, facts: PatientAgeFacts): SectionProgress[] {
  const sections: SectionProgress[] = [];
  if (identityShown({ ...context, mode: 'full' })) sections.push({ id: 'identity', done: identityRecorded(values) });
  const lastNeeded = nameParts(context.profile, 'full', context.keepsFullRecords).includes('last');
  sections.push({
    id: 'personal',
    done: filled(values.firstName) && (!lastNeeded || filled(values.lastName)) && (!context.keepsFullRecords || filled(values.dob) || filled(values.approximateAgeYears)),
  });
  if (context.keepsFullRecords && facts.infant) sections.push({ id: 'birth', done: filled(values.birthWeightGrams) && filled(values.gestationalAgeWeeks) });
  sections.push({ id: 'contact', done: filled(values.phone) && (!context.keepsFullRecords || filled(values.city) || filled(values.address)) });
  if (context.keepsFullRecords && facts.showGuardianDetails) sections.push({ id: 'guardian', done: !guardianNeeded(values, facts) || (filled(values.guardianName) && filled(values.guardianPhone)) });
  if (context.keepsFullRecords) sections.push({ id: 'emergency', done: filled(values.emergencyContactName) && filled(values.emergencyContactPhone) });
  return sections;
}

export function completeness(sections: SectionProgress[]): number {
  if (sections.length === 0) return 100;
  return Math.round((sections.filter((section) => section.done).length / sections.length) * 100);
}

export function duplicateCheckKey(payload: { fullName: string; phone?: string; dob?: string; cnic?: string }): string {
  return [payload.fullName.trim().toLowerCase(), payload.phone ?? '', payload.dob ?? '', payload.cnic ?? ''].join('|');
}

export function guardianRelationOf(stored: string | null | undefined, hasGuardian: boolean): GuardianRelation {
  if (stored && (GUARDIAN_RELATION as readonly string[]).includes(stored)) return stored as GuardianRelation;
  if (stored === 'mother' || stored === 'father') return 'parent';
  return hasGuardian ? 'other' : 'self';
}

export interface PatientRecord {
  id: string;
  mrn: string;
  fullName: string;
  firstName: string;
  lastName: string | null;
  middleName: string | null;
  secondLastName: string | null;
  fatherOrHusbandName: string | null;
  fullNameLocal: string | null;
  sex: string;
  dob: string | null;
  approximateAgeYears: number | null;
  birthWeightGrams: number | null;
  gestationalAgeWeeks: number | null;
  gestationalAgeDays: number | null;
  cnic: string | null;
  idType: string | null;
  idCountry: string | null;
  idExpiry: string | null;
  idMissingReason: string | null;
  nationality: string | null;
  occupationCode: string | null;
  phone: string | null;
  altPhone: string | null;
  email: string | null;
  bloodGroup: string;
  category: string;
  maritalStatus: string;
  address: string | null;
  city: string | null;
  preferredLanguage: string;
  guardianName: string | null;
  guardianRelation: string | null;
  guardianPhone: string | null;
  emergencyContactName: string | null;
  emergencyContactPhone: string | null;
  notes: string | null;
  branchId: string | null;
  ageLabel: string;
  isDeceased?: boolean;
  photoUrl?: string | null;
}

function day(value: string | null): string | undefined {
  return value ? value.slice(0, 10) : undefined;
}

function text(value: string | null): string {
  return value ?? '';
}

function count(value: number | null): number | undefined {
  return value === null ? undefined : value;
}

export function formValuesOf(record: PatientRecord): RegisterPatientInput {
  return {
    firstName: record.firstName,
    lastName: text(record.lastName),
    middleName: text(record.middleName),
    secondLastName: text(record.secondLastName),
    fatherOrHusbandName: text(record.fatherOrHusbandName),
    fullNameLocal: text(record.fullNameLocal),
    sex: record.sex as RegisterPatientInput['sex'],
    dob: day(record.dob),
    approximateAgeYears: record.dob ? undefined : count(record.approximateAgeYears),
    birthWeightGrams: count(record.birthWeightGrams),
    gestationalAgeWeeks: count(record.gestationalAgeWeeks),
    gestationalAgeDays: count(record.gestationalAgeDays),
    cnic: text(record.cnic),
    idType: (record.idType ?? undefined) as RegisterPatientInput['idType'],
    idCountry: text(record.idCountry),
    idExpiry: day(record.idExpiry),
    idMissingReason: (record.idMissingReason ?? undefined) as RegisterPatientInput['idMissingReason'],
    nationality: text(record.nationality),
    occupationCode: text(record.occupationCode),
    phone: text(record.phone),
    altPhone: text(record.altPhone),
    email: text(record.email),
    bloodGroup: record.bloodGroup as RegisterPatientInput['bloodGroup'],
    category: record.category as RegisterPatientInput['category'],
    maritalStatus: record.maritalStatus as RegisterPatientInput['maritalStatus'],
    address: text(record.address),
    city: text(record.city),
    preferredLanguage: record.preferredLanguage as RegisterPatientInput['preferredLanguage'],
    guardianName: text(record.guardianName),
    guardianRelation: guardianRelationOf(record.guardianRelation, Boolean(record.guardianName?.trim())),
    guardianPhone: text(record.guardianPhone),
    emergencyContactName: text(record.emergencyContactName),
    emergencyContactPhone: text(record.emergencyContactPhone),
    notes: text(record.notes),
    branchId: record.branchId ?? undefined,
    acknowledgeDuplicate: false,
  };
}

const IDENTITY_KEYS = ['cnic', 'idType', 'idCountry', 'idExpiry', 'idMissingReason', 'nationality'] as const;
const AGE_KEYS = ['dob', 'approximateAgeYears'] as const;
const GESTATION_KEYS = ['gestationalAgeWeeks', 'gestationalAgeDays'] as const;
const GUARDIAN_KEYS = ['guardianRelation', 'guardianName', 'guardianPhone'] as const;
const EDITABLE_KEYS = [
  'firstName',
  'lastName',
  'middleName',
  'secondLastName',
  'fatherOrHusbandName',
  'fullNameLocal',
  'sex',
  ...AGE_KEYS,
  'birthWeightGrams',
  ...GESTATION_KEYS,
  ...IDENTITY_KEYS,
  'occupationCode',
  'phone',
  'altPhone',
  'email',
  'bloodGroup',
  'category',
  'maritalStatus',
  'address',
  'city',
  'preferredLanguage',
  ...GUARDIAN_KEYS,
  'emergencyContactName',
  'emergencyContactPhone',
  'notes',
] as const satisfies readonly (keyof RegisterPatientInput & keyof UpdatePatientInput)[];

type EditableKey = (typeof EDITABLE_KEYS)[number];

function normalised(value: unknown): unknown {
  if (typeof value === 'string') return value.trim() === '' ? null : value.trim();
  if (typeof value === 'number') return Number.isNaN(value) ? null : value;
  return value ?? null;
}

export function changedKeys(initial: Partial<RegisterPatientInput>, values: Partial<RegisterPatientInput>): EditableKey[] {
  return EDITABLE_KEYS.filter((key) => normalised(initial[key]) !== normalised(values[key]));
}

export function hasChanges(initial: Partial<RegisterPatientInput>, values: Partial<RegisterPatientInput>): boolean {
  return changedKeys(initial, values).some((key) => key !== 'idType');
}

export type EditProblem = 'remove_number_needs_no_document' | 'no_document_needs_reason';

export function editPayload(initial: Partial<RegisterPatientInput>, values: Partial<RegisterPatientInput>): { payload: Record<string, unknown>; problem: EditProblem | null } {
  const changed = new Set<EditableKey>(changedKeys(initial, values));
  const touches = (group: readonly EditableKey[]) => group.some((key) => changed.has(key));
  if (touches(AGE_KEYS)) AGE_KEYS.forEach((key) => changed.add(key));
  if (touches(GESTATION_KEYS)) GESTATION_KEYS.forEach((key) => changed.add(key));
  if (touches(GUARDIAN_KEYS)) GUARDIAN_KEYS.forEach((key) => changed.add(key));

  const payload: Record<string, unknown> = {};
  for (const key of changed) {
    if ((IDENTITY_KEYS as readonly string[]).includes(key)) continue;
    payload[key] = normalised(values[key]);
  }

  if (touches(IDENTITY_KEYS)) {
    const number = normalised(values.cnic);
    if (values.idType === 'no_document') {
      if (!values.idMissingReason) return { payload, problem: 'no_document_needs_reason' };
      Object.assign(payload, { idType: 'no_document', idMissingReason: values.idMissingReason });
    } else if (number === null) {
      if (normalised(initial.cnic) !== null) return { payload, problem: 'remove_number_needs_no_document' };
    } else {
      const typeChanged = changed.has('idType') && Boolean(values.idType);
      if (changed.has('cnic') || typeChanged) payload.cnic = number;
      if (typeChanged) payload.idType = values.idType;
      if ((changed.has('idCountry') || typeChanged) && normalised(values.idCountry) !== null) payload.idCountry = normalised(values.idCountry);
      if (changed.has('idExpiry') && normalised(values.idExpiry) !== null) payload.idExpiry = normalised(values.idExpiry);
    }
    if (changed.has('nationality') && normalised(values.nationality) !== null) payload.nationality = normalised(values.nationality);
  }

  for (const key of ['firstName', 'sex', 'bloodGroup', 'category', 'maritalStatus', 'preferredLanguage', 'guardianRelation'] as const) {
    if (payload[key] === null) delete payload[key];
  }
  return { payload, problem: null };
}

export function validationKeysForEdit(initial: Partial<RegisterPatientInput>, values: Partial<RegisterPatientInput>): Set<string> {
  const changed = new Set<string>(changedKeys(initial, values));
  changed.add('firstName');
  if ([...AGE_KEYS, ...GUARDIAN_KEYS].some((key) => changed.has(key))) GUARDIAN_KEYS.forEach((key) => changed.add(key));
  if (GESTATION_KEYS.some((key) => changed.has(key))) GESTATION_KEYS.forEach((key) => changed.add(key));
  return changed;
}
